/**
 * Approximate STI (speech transmission index) bar chart — paint layer only.
 * See `../sti.ts` for the C50-derived estimate and its "simplified
 * approximation, not a certified measurement" caveat. Ports web's
 * `web/src/components/room/charts/stiDraw.ts` quality-band bar-colour
 * thresholds exactly, onto mobile's `INK` palette.
 */

import { setupCanvas, drawEmptyMessage, drawLogFreqAxisGrid, drawBars, INK, type DrawBar } from './_shared';
import { computeStiChartGeometry } from '../sti';
import type { ChartPad } from '../layout';
import type { RT60DecayCurve } from '../registry';

export const STI_PAD: ChartPad = { t: 14, r: 14, b: 26, l: 28 };

export function stiBarColor(sti: number | null): string {
  if (sti == null) return INK.gridMinor;
  if (sti >= 0.6) return INK.good;
  if (sti >= 0.45) return INK.neutralBar;
  return INK.warn;
}

export interface DrawStiOpts {
  decay: RT60DecayCurve;
  width: number;
  height: number;
}

export function drawStiCanvas(canvas: HTMLCanvasElement, opts: DrawStiOpts): void {
  const s = setupCanvas(canvas, opts.width, opts.height);
  if (!s) return;
  const { ctx, W, H } = s;

  const geo = computeStiChartGeometry({
    decay: opts.decay,
    width: opts.width,
    height: opts.height,
    pad: STI_PAD,
    measureWidth: (label) => ctx.measureText(label).width,
  });

  if (geo.empty) {
    drawEmptyMessage(ctx, W, H, 'No decay curve yet — ≥200 Hz only');
    return;
  }

  drawLogFreqAxisGrid(ctx, geo.frame, geo.xTicks, geo.yTicks);

  const bars: DrawBar[] = geo.bars.map((bar) => ({
    x0: bar.x0, x1: bar.x1, y: bar.y, color: stiBarColor(bar.sti),
  }));
  drawBars(ctx, geo.frame, bars, 0);
}
